<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte'
    import { printerParams } from './stores'
    import { SharePageDto } from './share-page.dto'
    import type { SharePageEntity } from '../../modules/toolsApi/share-pages/share-page.entity'
    import Zflow from './z/zflow.svelte'
    import Zgrid from './z/zgrid.svelte'
    import Zgrid2 from './z/zgrid2.svelte'
    import { SharePagesService } from '../../modules/toolsApi/share-pages/share-pages.service'
    import { SharePagesRepository } from '../../modules/toolsApi/share-pages/share-pages.repository'
    import { CompaniesService } from '../../modules/usersApi/companies/companies.service'
    import { CompaniesRepository } from '../../modules/usersApi/companies/companies.repository'
    import QuotationPrint from '../pos/quotation/quotation-print.svelte'
    import dayjs from 'dayjs'
    import SharePageHeader from './share-page-header.svelte'
    import SharePageFooter from './share-page-footer.svelte'
    import { ConversionsService } from '../../modules/utils/conversions.service'

    const dispatch = createEventDispatcher()

    let presentation: boolean = false

    let createdAt = new Date()

    let company = window.store?.company
    let printMediaQuery: MediaQueryList = null

    onMount(async () => {
        window.addEventListener('afterprint', onAfterPrintEvent)
        printMediaQuery = window.matchMedia('print')
        // addListener é o depreciado, mas é o único que o Safari antigo (< 14) tem — e é justamente
        // nele que o afterprint falta, então o caminho da media query precisa funcionar lá.
        if (printMediaQuery.addEventListener) printMediaQuery.addEventListener('change', onPrintMediaChange)
        else printMediaQuery.addListener(onPrintMediaChange)
        await evaluateSavedContent()
    })

    onDestroy(() => {
        window.removeEventListener('afterprint', onAfterPrintEvent)
        if (!printMediaQuery) return
        if (printMediaQuery.removeEventListener) printMediaQuery.removeEventListener('change', onPrintMediaChange)
        else printMediaQuery.removeListener(onPrintMediaChange)
    })

    function expandToken(t: string) {
        t = t?.replace(':', '_')
        let sharePageId = t.split('_')[0]
        let token = t.split('_')[1]
        if (!t.includes('-')) {
            const conversions = new ConversionsService()
            sharePageId = conversions.base58ToUuid(sharePageId)
            token = conversions.base58ToUuid(token)
        }
        return { sharePageId, token }
    }

    let error = false
    let sharedPage: SharePageEntity = null
    async function evaluateSavedContent() {
        if (!window.location.pathname.startsWith('/public/share')) return
        const urlParams = new URLSearchParams(window.location.search)
        let id = urlParams.get('id')
        if (!id) return null
        const data: { sharePageId: string; token: string } = expandToken(id)
        if (!data) return
        const spS = new SharePagesService(new SharePagesRepository())
        sharedPage = await spS.publicGetOneByToken(data.sharePageId, data.token)
        if (!sharedPage) {
            error = true
            window.alert('Este documento não está mais disponível.\nO link pode estar errado ou o tempo de compartilhamento expirou.')
            return
        }
        if (!company) {
            const cS = new CompaniesService(new CompaniesRepository())
            company = await cS.getPublicCompany(sharedPage.companyId)
        }
        console.log('sharedPage', sharedPage)

        _printerParams = new SharePageDto()
        for (const item of sharedPage.data) {
            if (item.type === 'zgrid') {
                _printerParams.component.push(Zgrid)
                _printerParams.props.push(item.data)
            }
            if (item.type === 'zgrid2') {
                _printerParams.component.push(Zgrid2)
                _printerParams.props.push(item.data)
            }
            if (item.type === 'quotation-print') {
                _printerParams.component.push(QuotationPrint)
                _printerParams.props.push(item.data)
            }
        }
        createdAt = new Date(sharedPage.createdAt)
        dispatch('printing', true)
        await tick()
        _printerParams = _printerParams
        presentation = true
    }

    // Quem passa hideHeader+hideFooter traz a própria moldura (caso do Forms V2, que tem timbre
    // próprio — ver form-v2-print-frame.svelte). Nesse caso a tabela daqui não teria thead/tfoot
    // pra repetir por página: seria só um invólucro, e aninhar tabelas atrapalha a repetição do
    // cabeçalho de quem está dentro. Por isso renderizamos o conteúdo direto.
    $: useOwnFrame = !!_printerParams?.hideHeader && !!_printerParams?.hideFooter

    let _printerParams: SharePageDto = null
    printerParams.subscribe((value) => {
        company = window.store?.company
        _printerParams = value
        if (value) {
            logPrint('pedido recebido, waitLoadedEvent=' + !!value.waitLoadedEvent)
            // Armado já aqui, e não só no goPrint(): com waitLoadedEvent, um componente que nunca
            // emite `loaded` (uma falha no carregamento, por exemplo) deixava a tela de espera para
            // sempre, porque o único timeout existia depois do window.print().
            armResetFallback(30000, 'timeout: o evento loaded nunca chegou')
            if (!_printerParams.waitLoadedEvent) goPrint()
        }
    })

    let printed = false
    function onComponentLoaded() {
        if (!printed) {
            printed = true
            logPrint('conteúdo montado (evento loaded)')
            goPrint()
        }
    }

    // Sair do estado de impressão não pode depender de um sinal só: quando a impressão não chega a
    // começar (ver checkPrintStarted), nenhum evento de impressão acontece e, sem rede de segurança,
    // a tela de espera fica parada. Daí três fontes independentes — `afterprint`, a saída da media
    // query `print` e um timeout — mais o botão Cancelar e a tecla Esc.
    // resetAfterPrint() é idempotente: ser chamado várias vezes é inofensivo.
    let resetFallbackTimer: ReturnType<typeof setTimeout> = null
    let printStartedCheckTimer: ReturnType<typeof setTimeout> = null
    let needsManualPrint = false
    // Sem ter entrado em `print` antes, "deixou de ser print" não significa fim de impressão alguma.
    let enteredPrintMedia = false

    // Rastro de cada passo da impressão: é o que distingue "nunca chegamos a chamar
    // window.print()" de "chamamos e o navegador não abriu a caixa de impressão". Sem ele, um
    // relato de tela travada não diz em qual dos dois olhar.
    //
    // Vai por window.log (console + New Relic, com companyId e rota) e não por console.log direto:
    // o agente só captura o console nos domínios publicados, e sem os atributos não há como achar
    // no New Relic a impressão de um cliente específico.
    //
    // Nível `warn` de propósito, e temporário: o filtro de nível dos logs de browser é configurado
    // no New Relic, e `debug` pode ser descartado lá — o que justamente apagaria o rastro do caso
    // que estamos investigando. Baixar para `debug` quando o problema do Safari estiver fechado.
    function logPrint(step: string, extra: any = undefined) {
        window.log?.('[impressão] ' + step, 'warn', { userAgent: navigator.userAgent, ...(extra ?? {}) })
    }

    /**
     * Estado da página no instante da chamada, para o caso em que o navegador não abre a caixa de
     * impressão e não dá nenhum retorno sobre o motivo. Duas hipóteses já caíram por falta de dado
     * (a de que o Safari exigia um gesto do usuário, e a de que o print() era bloqueante), então aqui
     * vão as medidas que distinguem as que sobraram: documento sem altura/conteúdo para imprimir,
     * janela sem foco, ou aba em segundo plano.
     */
    function printDiagnostics() {
        const outer = document.querySelector('.table_print_outer') as HTMLElement
        return {
            bodyHeight: document.body?.scrollHeight ?? null,
            docHeight: document.documentElement?.scrollHeight ?? null,
            contentHeight: outer?.scrollHeight ?? null,
            contentWidth: outer?.scrollWidth ?? null,
            contentChildren: outer?.children?.length ?? null,
            visibility: document.visibilityState,
            hasFocus: document.hasFocus?.() ?? null,
            printMedia: window.matchMedia('print').matches,
        }
    }

    function armResetFallback(ms: number, origin: string) {
        clearTimeout(resetFallbackTimer)
        resetFallbackTimer = setTimeout(() => resetAfterPrint(origin), ms)
    }

    function resetAfterPrint(origin: string) {
        clearTimeout(resetFallbackTimer)
        clearTimeout(printStartedCheckTimer)
        enteredPrintMedia = false
        needsManualPrint = false
        if (_printerParams) logPrint('encerrado por: ' + origin)
        window.printer(null)
        printed = false
        dispatch('printing', false)
    }

    /**
     * O Safari ignora `window.print()` chamado fora de um gesto do usuário: a chamada retorna na
     * hora, a caixa de impressão não abre e nada avisa que foi descartada — a pessoa só vê a tela de
     * espera parada. Confirmado nos logs: nas tentativas pelo botão do app, o print() retornava no
     * mesmo segundo, nunca havia entrada em modo de impressão e o ciclo só terminava pelo timeout;
     * no mesmo Safari, imprimir pelo Cmd+P (um gesto) funcionava normalmente. Chrome e Firefox não
     * exigem isso, e por isso o problema só aparecia ali.
     *
     * Como o conteúdo precisa carregar antes de imprimir, não há como ficar dentro do gesto original.
     * Então: segue tentando automaticamente e, se a impressão não tiver começado, oferece o botão —
     * o clique devolve o gesto que o Safari exige. Nos navegadores em que o print() é bloqueante,
     * esta verificação só roda depois que a caixa fecha, quando já entramos em modo de impressão ou
     * o ciclo foi encerrado, então o botão nunca aparece.
     */
    function checkPrintStarted() {
        if (!_printerParams) return
        if (enteredPrintMedia) return
        needsManualPrint = true
        logPrint('o navegador descartou window.print(); exibindo o botão de imprimir manual')
    }

    function manualPrint() {
        logPrint('impressão pedida pelo botão (gesto do usuário)', printDiagnostics())
        window.print()
        armResetFallback(15000, 'timeout após a impressão manual')
    }

    const onAfterPrintEvent = () => resetAfterPrint('evento afterprint')

    function onPrintMediaChange(event: { matches: boolean }) {
        if (event.matches) {
            enteredPrintMedia = true
            logPrint('navegador entrou em modo de impressão')
            return
        }
        if (enteredPrintMedia) resetAfterPrint('saída do modo de impressão (media query)')
    }

    function onWindowKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && _printerParams) resetAfterPrint('tecla Esc')
    }

    async function goPrint() {
        dispatch('printing', !!_printerParams)
        await tick()
        setTimeout(() => {
            logPrint('chamando window.print()', printDiagnostics())
            window.print()
            logPrint('window.print() retornou', printDiagnostics())
            // 1,2s é folga de sobra: onde a impressão realmente começa, a media query `print` acusa
            // isso muito antes disso.
            clearTimeout(printStartedCheckTimer)
            printStartedCheckTimer = setTimeout(checkPrintStarted, 1200)
        }, 1500)
        armResetFallback(15000, 'timeout após window.print()')
    }
</script>

<svelte:window on:keydown={onWindowKeydown} />

{#if _printerParams && company}
    {#if !presentation}
        <!-- Enquanto o conteúdo é montado o app inteiro fica escondido (ver .printing em App.svelte),
             então esta camada é tudo o que a pessoa vê. Ela precisa dizer o que está acontecendo e
             oferecer uma saída: se algum navegador não avisar que a impressão terminou, sem isto a
             tela cinza vira um travamento sem escapatória além de recarregar a página. -->
        <div class="print_wait">
            <div class="print_wait_box">
                {#if needsManualPrint}
                    <div>O documento está pronto. Clique em Imprimir para abrir a caixa de impressão.</div>
                    <button type="button" class="print_wait_print" on:click={manualPrint}>Imprimir</button>
                {:else}
                    <div>Preparando impressão...</div>
                {/if}
                <button type="button" class="print_wait_cancel" on:click={() => resetAfterPrint('botão Cancelar')}>Cancelar</button>
            </div>
        </div>
    {/if}

    <div class="table_print_outer" class:table_print_outer_presentation={presentation}>
        {#if useOwnFrame}
            <Zflow>
                {#each _printerParams?.component as pp, index}
                    <svelte:component this={pp} on:loaded={onComponentLoaded} {..._printerParams?.props[index]}></svelte:component>
                {/each}
            </Zflow>
        {:else}
            <table class="table_print">
                {#if !_printerParams?.hideHeader}
                    <thead><tr><td> <div class="header-space">&nbsp;</div> </td></tr></thead>
                {/if}
                <tbody
                    ><tr
                        ><td>
                            <Zflow>
                                {#each _printerParams?.component as pp, index}
                                    <svelte:component this={pp} on:loaded={onComponentLoaded} {..._printerParams?.props[index]}></svelte:component>
                                {/each}
                            </Zflow>
                        </td></tr
                    ></tbody
                >
                {#if !_printerParams?.hideFooter}
                    <tfoot><tr><td> <div class="footer-space">&nbsp;</div> </td></tr></tfoot>
                {/if}
            </table>
        {/if}

        {#if !_printerParams?.hideWaterMark}
            <div class="table_print_icon">
                <img src={company.icon} />
            </div>
        {/if}

        {#if !_printerParams?.hideHeader}
            <SharePageHeader {company}></SharePageHeader>
        {/if}
        {#if !_printerParams?.hideFooter}
            <SharePageFooter {company} {createdAt}></SharePageFooter>
        {/if}
    </div>
{/if}

<style>
    .table_print {
        width: 100%;
        min-height: 100% !important;
        background-color: unset !important;
        word-break: break-word;
    }
    .table_print td {
        vertical-align: top;
    }
    .table_print tr {
    }
    .table_print .row {
        margin-left: 0px !important;
        margin-right: 0px !important;
    }
    .table_print *,
    .table_print :global(.zgrid *),
    .table_print :global(.zframe *) {
        background-color: unset !important;
    }

    .table_print :global(.zgrid .zgrid_td_mobile_title) {
        text-shadow: unset;
        color: unset;
    }
    .header-space {
        height: 60px !important;
    }
    .footer-space {
        height: 110px !important;
    }

    .print_wait {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        background-color: #6c757d !important;
        border-left-width: 100%;
        height: 100% !important;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .print_wait_box {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: #fff;
        font-size: 0.95rem;
    }
    .print_wait_print {
        border: none;
        background: #0d6efd;
        color: #fff;
        border-radius: 6px;
        padding: 10px 28px;
        font-weight: 600;
        cursor: pointer;
    }
    .print_wait_cancel {
        border: 1px solid #ffffff80;
        background: transparent;
        color: #fff;
        border-radius: 6px;
        padding: 6px 18px;
        cursor: pointer;
    }
    .table_print * {
        max-width: 100% !important;
    }
    .table_print_outer_presentation .table_print {
        max-width: 1100px !important;
        margin: 0 auto;
        margin-top: 16px;
        background-color: white !important;
    }
    @media print {
        .print_wait {
            display: none;
        }
        .table_print_outer_presentation .table_print {
            margin-top: unset;
        }
    }
    .table_print_icon {
        opacity: 0.1;
        position: fixed;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        top: calc(50vh - 280px);
    }
    .table_print_icon img {
        height: 560px !important;
        max-height: 90%;
    }
</style>
